import React, { useRef } from 'react';
import Image from 'next/image';
import SwiperCore, { Navigation, Scrollbar } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import swiperEndImage from '@assets/images/swiper-end.png';
import swipeLeft from '@assets/images/swipe-left.svg';
import swipeRight from '@assets/images/swipe-right.svg';

import styles from '../swiper.module.css';
import 'swiper/css';
import 'swiper/css/scrollbar';

import HotelCard, { HotelCardProps } from './HotelCard';
import Button from '../Button';

type HotelSectionProps = {
  hotels: HotelCardProps[];
  headline: string;
};

SwiperCore.use([Navigation, Scrollbar]);

export default function HotelSection({ hotels, headline }: HotelSectionProps) {
  const navigationPrevRef = useRef<HTMLDivElement>(null);
  const navigationNextRef = useRef<HTMLDivElement>(null);
  return (
    <div
      id="hotel-section"
      className="flex flex-col d:598 pt-16 gap-6 text-rp-neutral border-b border-rp-primary-redesign d:h-704 d:py-0 d:justify-center"
    >
      <div className="flex justify-between px-4 d:px-0 d:mx-122">
        <h1 className="text-2xl d:text-32">{headline}</h1>
        <div className="hidden d:flex">
          <span
            ref={navigationPrevRef}
            className="flex justify-center items-center bg-rp-inverted w-11 h-11 cursor-pointer rounded"
          >
            <Image src={swipeLeft} alt="swipe-left" />
          </span>
          <span
            ref={navigationNextRef}
            className="flex justify-center items-center bg-rp-inverted w-11 h-11 cursor-pointer rounded"
          >
            <Image src={swipeRight} alt="swipe-right" />
          </span>
        </div>
      </div>
      <Swiper
        spaceBetween={16}
        slidesPerView="auto"
        preloadImages={false}
        className={styles.hotelsSwiper}
        pagination={false}
        lazy={{
          loadPrevNext: true,
        }}
        slidesOffsetBefore={16}
        slidesOffsetAfter={16}
        scrollbar={{
          enabled: false,
          dragClass: styles.hotelsSwiperScrollbarBar,
          horizontalClass: styles.hotelsSwiperScrollbar,
        }}
        navigation={{
          prevEl: navigationPrevRef.current!,
          nextEl: navigationNextRef.current!,
        }}
        noSwiping
        breakpoints={{
          850: {
            scrollbar: {
              draggable: true,
              dragSize: 220,
              dragClass: styles.hotelsSwiperScrollbarBar,
              horizontalClass: styles.hotelsSwiperScrollbar,
              enabled: true,
            },
            slidesOffsetAfter: 122,
            slidesOffsetBefore: 122,
            pagination: {
              clickable: true,
            },
            spaceBetween: 24,
          },
        }}
      >
        {hotels.map((hotel) => (
          <SwiperSlide key={hotel.name} className={styles.hotelsSwiperSlide}>
            <HotelCard
              imageUrl={hotel.imageUrl}
              name={hotel.name}
              location={hotel.location}
              rating={hotel.rating}
              reviews={hotel.reviews}
              description={hotel.description}
              url={hotel.url}
            />
          </SwiperSlide>
        ))}
        <SwiperSlide className={styles.hotelsSwiperSlide}>
          <div className="flex items-center justify-center h-196 relative d:h-236">
            <Image src={swiperEndImage} alt="swiper-end" className="absolute w-full h-full" />
            <Button
              size="lg"
              color="secondary"
              href="https://www.resortpass.com/browse-hotels"
              classes="absolute"
              type="link"
            >
              View All Hotels
            </Button>
          </div>
        </SwiperSlide>
      </Swiper>
    </div>
  );
}
